import { clsx } from 'clsx';

/**
 * Inline SVG, stroke-based, 24×24 viewBox, 1.75 stroke, round caps and joins.
 *
 * **No emoji anywhere in the product** — UI-UX-SPEC §2, and it is not a style preference. An emoji
 * renders as a different picture on every platform, ignores the palette entirely, and gets read
 * aloud by a screen reader as its Unicode name. These inherit `currentColor`, so an icon in the
 * dark rail and the same icon on a white card are the same icon.
 *
 * Decorative by default: the label beside an icon is the accessible name, and a nav item that
 * announces "grid, Dashboard" is worse than one that announces "Dashboard". Pass a `title` only
 * when the icon stands alone.
 */
export type IconName =
  | 'dashboard'
  | 'league'
  | 'players'
  | 'tasks'
  | 'notes'
  | 'kit'
  | 'playbook'
  | 'settings'
  | 'market'
  | 'applications'
  | 'fulfillment'
  | 'tiles'
  | 'quality'
  | 'audit'
  | 'directors'
  | 'templates'
  | 'today'
  | 'standings'
  | 'you'
  | 'chevronDown'
  | 'chevronRight'
  | 'signOut';

/**
 * Path data only — every icon shares the wrapper's viewBox, stroke and joins, so a new one is a
 * line here rather than another copy of the same six attributes.
 */
const PATHS: Record<IconName, string> = {
  dashboard: 'M4 4h7v7H4zM13 4h7v4h-7zM13 10h7v10h-7zM4 13h7v7H4z',
  league: 'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4',
  players: 'M3 20v-1a5 5 0 0 1 5-5h1a5 5 0 0 1 5 5v1M11 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0zM16 14a5 5 0 0 1 5 5v1M15 5.2a3 3 0 0 1 0 5.6',
  tasks: 'M9 6h11M9 12h11M9 18h11M4 6l1.2 1.2L7.5 5M4 12l1.2 1.2L7.5 11M4 18l1.2 1.2L7.5 17',
  notes: 'M20 14a2 2 0 0 1-2 2H8l-4 4V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2z',
  kit: 'M3 8l9-4 9 4-9 4zM3 8v8l9 4 9-4V8M12 12v8',
  playbook: 'M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2zM8 7h7M8 11h7',
  settings: 'M4 7h10M18 7h2M4 17h2M10 17h10M16 4v6M8 14v6',
  market: 'M12 21s7-6.3 7-11a7 7 0 1 0-14 0c0 4.7 7 11 7 11zM12 12a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
  applications: 'M8 4H6a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2M9 3h6v3H9zM8 12h8M8 16h5',
  fulfillment: 'M3 7h11v9H3zM14 10h4l3 3v3h-7zM7.5 19a1.8 1.8 0 1 0 0-3.6 1.8 1.8 0 0 0 0 3.6zM17.5 19a1.8 1.8 0 1 0 0-3.6 1.8 1.8 0 0 0 0 3.6z',
  tiles: 'M4 4h6v7H4zM14 4h6v7h-6zM4 13h6v7H4zM14 13h6v7h-6z',
  quality: 'M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.5 9.7l5.9-.9z',
  audit: 'M3.5 12a8.5 8.5 0 1 0 2.6-6.1M3.5 4.5V9h4.5M12 7.5V12l3 2',
  directors: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4.5 21a7.5 7.5 0 0 1 15 0',
  templates: 'M4 5h16v14H4zM4 9h16M9 9v10',
  today: 'M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z',
  standings: 'M5 20V11M12 20V4M19 20v-6M3 20h18',
  you: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4.5 21a7.5 7.5 0 0 1 15 0',
  chevronDown: 'M6 9.5l6 6 6-6',
  chevronRight: 'M9.5 6l6 6-6 6',
  signOut: 'M15 5H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h9M18.5 12H10M15.5 8.5L19 12l-3.5 3.5',
};

export function Icon({
  name,
  size = 18,
  title,
  className,
}: {
  name: IconName;
  size?: number;
  /** Give this ONLY when the icon has no visible label beside it. */
  title?: string;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      className={clsx('shrink-0', className)}
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
